import { useTranslation } from '@repo/translations/use-translation';
import { getSiteBrand } from '@repo/utils/site';
import { lazy, Suspense, SVGProps } from 'react';

import { useSiteContext } from '@zooplus/commons-lang';

export interface ProgramLogoProps {
  width?: SVGProps<SVGSVGElement>['width'];
  height?: SVGProps<SVGSVGElement>['height'];
  variant?: 'light' | 'dark';
}

const logoVariants = {
  light: {
    zooplus: lazy(() =>
      import('./LogoZooplusClub').then((module) => ({ default: module.LogoZooplusClub })),
    ),
    zoohit: lazy(() =>
      import('./LogoZoohitClub').then((module) => ({ default: module.LogoZoohitClub })),
    ),
    zoochic: lazy(() =>
      import('./LogoZoochicClub').then((module) => ({ default: module.LogoZoochicClub })),
    ),
    bitiba: null,
  },
  dark: {
    zooplus: lazy(() =>
      import('./LogoZooplusClubDark').then((module) => ({ default: module.LogoZooplusClubDark })),
    ),
    zoohit: lazy(() =>
      import('./LogoZoohitClubDark').then((module) => ({ default: module.LogoZoohitClubDark })),
    ),
    zoochic: lazy(() =>
      import('./LogoZoochicClubDark').then((module) => ({ default: module.LogoZoochicClubDark })),
    ),
    bitiba: null,
  },
};

/**
 * The `ProgramLogo` component displays the Loyalty Program logo according to the current site.
 *
 * It supports light (default) and dark variants, and allows customization of width and height.
 *
 * The component uses lazy loading to optimize performance and accessibility (image role and text description) is already baked in.
 *
 * **N.B.** When specifying dimensions, it is generally recommended to only set one dimension at a time (either width or height)
 * to maintain the logo's aspect ratio.
 * Logos have different sizes depending on the brand, so it is important to only set one dimension to avoid distortion.
 * Generally, prefer setting the height to ensure consistent vertical spacing across different logos.
 */
export const ProgramLogo = ({ width, height, variant = 'light' }: ProgramLogoProps) => {
  const {
    site: { domain },
  } = useSiteContext();
  const { t } = useTranslation();

  const siteBrand = getSiteBrand(domain);

  const Logo = logoVariants[variant][siteBrand];

  if (Logo) {
    return (
      <Suspense fallback={null}>
        <Logo
          width={width}
          height={height}
          aria-label={t('loyalty-program.program-name')}
          role="img"
        />
      </Suspense>
    );
  }

  return null;
};
